import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import TradeForm from "./components/TradeForm";
import OrderBookCard from "./components/OrderBookCard";
import OrderHistory from "./components/OrderHistory";
import ApiKeyInput from "./components/ApiKeyInput";
import {
  connectWebSocket,
  disconnectWebSocket,
  getConnectionStatus,
  registerMessageHandler,
  registerPortfolioHandler,
  resetWebSocketConnection,
  registerSettlementHandler,
  registerCounterHandler,
  registerErrorHandler,
  registerDrawUpdateHandler,
  registerRoleSignalHandler,
  registerCaseLoadedHandler,
  registerMatchedTradesHandler,
  sendMessage,
  getCurrentUserId,
  getCurrentTeamId,
  getCurrentTeamName,
} from "./services/websocket";
import Portfolio from "./components/Portfolio";
import PortfolioSummary from "./components/PortfolioSummary";
import ToastContainer from "./components/Toast";
import DrawVisualization from "./components/DrawVisualization";
import RolePanel from "./components/RolePanel";
import AdminPanel from "./components/AdminPanel";

const ROLE_INFO_STORAGE_KEY = "four_color_role_info";

function App() {
  const [apiKey, setApiKey] = useState(null);
  const [selectedContract, setSelectedContract] = useState("A");
  const [marketData, setMarketData] = useState({});
  const [userPosition, setUserPosition] = useState({
    balance: 0,
    positions: {},
  });
  const [priceHistory, setPriceHistory] = useState({});
  const [tradingStatus, setTradingStatus] = useState({
    symbols: [],
    running: true,
    time_remaining: null,
  });
  const contracts = (tradingStatus.symbols || []).map((sym) => ({ id: sym, name: sym }));
  const [wsConnection, setWsConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("DISCONNECTED");
  const [orders, setOrders] = useState([]);
  const [activeTrades, setActiveTrades] = useState([]);
  const [cancelledTrades, setCancelledTrades] = useState([]);
  const [portfolio, setPortfolio] = useState({
    cash: { total: 0, locked: 0, unlocked: 0 },
    positions: {},
    active_trades: [],
    cancelled_trades: [],
    active_trade_count: 0,
  });
  const [toasts, setToasts] = useState([]);
  const knownTradeIds = useRef(null); // null = baseline not yet established
  const [orderHistoryOpen, setOrderHistoryOpen] = useState(false);
  const [matchedTrades, setMatchedTrades] = useState([]);
  const [settlementData, setSettlementData] = useState(null);
  const [isTradingOver, setIsTradingOver] = useState(false);
  const [settlementPollingInterval, setSettlementPollingInterval] =
    useState(null);
  const [userError, setUserError] = useState(null);
  const [drawState, setDrawState] = useState(null);
  const [roleInfo, setRoleInfo] = useState(null);
  const [caseActive, setCaseActive] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [vwapData, setVwapData] = useState(null);

  useEffect(() => {
    // Check for stored API key on component mount
    const storedApiKey = localStorage.getItem("trading_api_key");
    const storedRoleInfo = localStorage.getItem(ROLE_INFO_STORAGE_KEY);
    if (storedRoleInfo) {
      try {
        const parsedRoleInfo = JSON.parse(storedRoleInfo);
        setRoleInfo(parsedRoleInfo);
        setCaseActive(true);
      } catch (error) {
        localStorage.removeItem(ROLE_INFO_STORAGE_KEY);
      }
    }
    if (storedApiKey) {
      setApiKey(storedApiKey);
      initializeWebSocket(storedApiKey);
    }
  }, []);

  // Add a new useEffect to handle periodic portfolio data requests
  useEffect(() => {
    if (!wsConnection || connectionStatus !== "CONNECTED") return;

    // Function to request portfolio data
    const requestPortfolioData = () => {
      if (wsConnection.readyState === WebSocket.OPEN) {
        wsConnection.send(JSON.stringify({ command: "current_portfolio" }));
        wsConnection.send(JSON.stringify({ command: "matched_trades" }));
      }
    };

    // Request data immediately after connection
    requestPortfolioData();

    // Set up interval to request portfolio data periodically.
    const intervalId = setInterval(requestPortfolioData, 500);

    return () => clearInterval(intervalId);
  }, [wsConnection, connectionStatus]);

  useEffect(() => {
    const unregisterMatched = registerMatchedTradesHandler((trades) => {
      setMatchedTrades(trades || []);
    });

    // Fetch on mount
    sendMessage({ command: "matched_trades" });

    return () => {
      unregisterMatched();
    };
  }, []);

  // Register handler for settlement status
  useEffect(() => {
    const unregisterSettlementHandler = registerSettlementHandler((data) => {
      setSettlementData(data);
      setIsTradingOver(data.is_settled);
    });

    return () => {
      unregisterSettlementHandler();
    };
  }, []);

  // Show toast notifications for newly filled trades
  useEffect(() => {
    const currentIds = new Set(matchedTrades.map(t => t.trade_id || t.id));

    if (knownTradeIds.current === null) {
      // First load — establish baseline, no toasts for existing fills
      knownTradeIds.current = currentIds;
      return;
    }

    // Group new fills by symbol+side+price so a large matched order
    // produces one toast instead of one per fill
    const groups = {};
    for (const trade of matchedTrades) {
      const id = trade.trade_id || trade.id;
      if (!knownTradeIds.current.has(id)) {
        const side = (trade.side || "").toUpperCase();
        const price = Number(trade.price).toFixed(0);
        const key = `${side}-${trade.symbol}-${price}`;
        if (!groups[key]) groups[key] = { side, symbol: trade.symbol, price, qty: 0 };
        groups[key].qty += trade.quantity;
      }
    }

    for (const g of Object.values(groups)) {
      addToast(`Filled: ${g.side} ${g.qty} ${g.symbol} @ $${g.price}`, "success");
    }

    knownTradeIds.current = currentIds;
  }, [matchedTrades]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register handler for timer/status updates
  useEffect(() => {
    const unregisterCounter = registerCounterHandler((data) => {
      if (data.status) {
        setTradingStatus(data.status);
      }
      // Handle session reset - clear ALL local state
      if (data.command === "session_reset") {
        console.log("Session reset detected - clearing all state");
        setIsTradingOver(false);
        setSettlementData(null);
        setMarketData({});
        setPriceHistory({});
        setUserPosition({ balance: 10000, positions: {} });
        setPortfolio({
          cash: { total: 0, locked: 0, unlocked: 0 },
          positions: {},
          active_trades: [],
          cancelled_trades: [],
          active_trade_count: 0,
        });
        setActiveTrades([]);
        setCancelledTrades([]);
        setOrders([]);
        setMatchedTrades([]);
        knownTradeIds.current = null;
        setDrawState(null);
        setRoleInfo(null);
        setCaseActive(false);
        setVwapData(null);
        localStorage.removeItem(ROLE_INFO_STORAGE_KEY);
        if (data.status) {
          setTradingStatus(data.status);
        }
        // Request fresh portfolio data
        if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
          wsConnection.send(JSON.stringify({ command: "current_portfolio" }));
        }
      }
    });

    return () => {
      unregisterCounter();
    };
  }, [wsConnection]);

  // Auto-select first valid contract when symbol list changes
  useEffect(() => {
    const symbols = tradingStatus.symbols || [];
    if (symbols.length > 0 && !symbols.includes(selectedContract)) {
      setSelectedContract(symbols[0]);
    }
  }, [tradingStatus.symbols]);

  // Register handlers for Four-Color Draw
  useEffect(() => {
    const unregisterDraw = registerDrawUpdateHandler((state) => {
      setDrawState(state);
      setCaseActive(true);
    });

    const unregisterRole = registerRoleSignalHandler((signal) => {
      setRoleInfo(signal);
      localStorage.setItem(ROLE_INFO_STORAGE_KEY, JSON.stringify(signal));
      setCaseActive(true);
    });

    const unregisterCaseLoaded = registerCaseLoadedHandler((data) => {
      setCaseActive(true);
      setDrawState(null);
    });

    return () => {
      unregisterDraw();
      unregisterRole();
      unregisterCaseLoaded();
    };
  }, []);

  // Register handler for errors
  useEffect(() => {
    const unregisterError = registerErrorHandler((error) => {
      console.error("WebSocket error received:", error);
      if (error && error.error) {
        if (error.error === "Invalid API key") {
          // Clear stored key and force re-login
          localStorage.removeItem("trading_api_key");
          disconnectWebSocket();
          resetWebSocketConnection();
          setApiKey(null);
          setWsConnection(null);
          setConnectionStatus("DISCONNECTED");
        }
        setUserError(error.error);
      }
    });

    return () => {
      unregisterError();
    };
  }, []);

  // Add toast to the stack — cap at 3 visible at once
  const addToast = (message, type = "info", duration = 5000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => {
      const trimmed = prev.length >= 3 ? prev.slice(prev.length - 2) : prev;
      return [...trimmed, { id, message, type, duration }];
    });
  };

  // Remove toast from the stack
  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const initializeWebSocket = (key) => {
    // Try to connect with the API key
    const ws = connectWebSocket(key);
    setWsConnection(ws);

    // Update connection status periodically
    const statusInterval = setInterval(() => {
      const status = getConnectionStatus();
      setConnectionStatus(status);

      // Only attempt to reconnect if we're actually disconnected
      // and not already in a connecting state
      if (
        (status === "CLOSED" || status === "DISCONNECTED") &&
        !ws?.connecting
      ) {
        resetWebSocketConnection();
        const newWs = connectWebSocket(key);
        setWsConnection(newWs);
      }
    }, 1000);

    // Register handler for market data updates (data comes from message.data, status from message.status via handler)
    const unregisterMarketHandler = registerMessageHandler((data, msgStatus, vwap) => {
      setVwapData(vwap);
      setMarketData(data);

      // Update trading status from market_data broadcast if present
      if (msgStatus) {
        setTradingStatus(msgStatus);
        // If trading is not running, show settlement screen
        if (!msgStatus.running) {
          setIsTradingOver(true);
        }
      }

      // Update price history if mid price is available
      setPriceHistory((prevHistory) => {
        const newHistory = { ...prevHistory };
        const now = Date.now();
        // 5 minutes in milliseconds
        const fiveMinutes = 5 * 60 * 1000;

        Object.keys(data).forEach((symbol) => {
          if (data[symbol].mid_price !== null && data[symbol].mid_price > 0) {
            // Add new price point
            const updatedPriceHistory = [
              ...(prevHistory[symbol] || []),
              {
                timestamp: now,
                price: data[symbol].mid_price,
              },
            ];

            // Filter to keep only data points from the last 5 minutes
            newHistory[symbol] = updatedPriceHistory.filter(
              (point) => now - point.timestamp <= fiveMinutes
            );
          }
        });

        return newHistory;
      });
    });

    // Register handler for portfolio updates
    const unregisterPortfolioHandler = registerPortfolioHandler((data) => {
      setPortfolio(data);
      // Clear any user errors on successful portfolio load
      setUserError(null);
      // Detect admin status from resolved user_id
      const uid = getCurrentUserId();
      if (uid) setIsAdmin(uid.startsWith("admin"));

      // Process active trades and cancelled trades
      if (data.active_trades) {
        const formattedActiveTrades = data.active_trades.map((trade) => ({
          id: trade.trade_id || trade.order_id,
          order_id: trade.order_id,
          symbol: trade.symbol,
          side: trade.side,
          contractId: trade.symbol,
          timestamp: trade.timestamp * 1000, // Convert to milliseconds
          price: trade.price,
          quantity: trade.quantity,
          filled: trade.filled || 0,
          status: "active",
        }));
        setActiveTrades(formattedActiveTrades);
      }

      if (data.cancelled_trades) {
        const formattedCancelledTrades = data.cancelled_trades.map((trade) => ({
          id: trade.trade_id || trade.order_id,
          order_id: trade.order_id,
          symbol: trade.symbol,
          side: trade.side,
          contractId: trade.symbol,
          timestamp: trade.timestamp * 1000,
          price: trade.price,
          quantity: trade.quantity,
          filled: trade.filled || 0,
          status: "cancelled",
        }));
        setCancelledTrades(formattedCancelledTrades);
      }

      // Combine for backward compatibility
      setOrders([
        ...(data.active_trades || []).map((trade) => ({
          id: trade.trade_id || trade.order_id,
          order_id: trade.order_id,
          symbol: trade.symbol,
          side: trade.side,
          contractId: trade.symbol,
          timestamp: trade.timestamp * 1000,
          price: trade.price,
          quantity: trade.quantity,
          filled: trade.filled || 0,
          status: "active",
        })),
        ...(data.cancelled_trades || []).map((trade) => ({
          id: trade.trade_id || trade.order_id,
          order_id: trade.order_id,
          symbol: trade.symbol,
          side: trade.side,
          contractId: trade.symbol,
          timestamp: trade.timestamp * 1000,
          price: trade.price,
          quantity: trade.quantity,
          filled: trade.filled || 0,
          status: "cancelled",
        })),
      ]);
    });

    // Clean up function
    return () => {
      clearInterval(statusInterval);
      unregisterMarketHandler();
      unregisterPortfolioHandler();
      disconnectWebSocket();
    };
  };

  const handleApiKeySuccess = (key) => {
    // Make sure any previous connection is fully closed
    disconnectWebSocket();
    resetWebSocketConnection();

    // Small delay to ensure cleanup is complete
    setTimeout(() => {
      setApiKey(key);
      setUserError(null); // Clear any previous errors
      initializeWebSocket(key);
    }, 100);
  };

  // Add logout function
  const handleLogout = () => {
    knownTradeIds.current = null;
    // Clear API key from localStorage
    localStorage.removeItem("trading_api_key");
    // Disconnect WebSocket
    disconnectWebSocket();
    // Reset WebSocket connection to fully clean up
    resetWebSocketConnection();
    // Reset all state
    setApiKey(null);
    setUserError(null);
    setWsConnection(null);
    setConnectionStatus("DISCONNECTED");
    setRoleInfo(null);
    setDrawState(null);
    setCaseActive(false);
    localStorage.removeItem(ROLE_INFO_STORAGE_KEY);
    setPortfolio({
      cash: { total: 0, locked: 0, unlocked: 0 },
      positions: {},
      active_trades: [],
      cancelled_trades: [],
      active_trade_count: 0,
    });
    setActiveTrades([]);
    setCancelledTrades([]);
  };

  // Handle contract selection
  const handleContractSelect = (contractId) => {
    setSelectedContract(contractId);
  };

  // Handle order cancellation — send to backend + optimistic UI update
  const handleCancelOrder = (orderId) => {
    const trade = activeTrades.find((t) => t.id === orderId);
    if (trade) {
      sendMessage({
        command: "cancel_order",
        data: {
          symbol: trade.symbol,
          side: trade.side,
          order_id: trade.order_id,
        },
      });
    }

    // Optimistically update UI
    setActiveTrades((prev) => prev.filter((t) => t.id !== orderId));
    setCancelledTrades((prev) => [
      ...prev,
      ...activeTrades
        .filter((t) => t.id === orderId)
        .map((t) => ({ ...t, status: "cancelled" })),
    ]);
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId ? { ...order, status: "cancelled" } : order
      )
    );
  };

  // Add function to request settlement data
  const requestSettlementData = () => {
    if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
      wsConnection.send(JSON.stringify({ command: "get_settlement" }));
    }
  };

  // Add useEffect to handle settlement polling
  useEffect(() => {
    if (isTradingOver) {
      // Start polling every 5 seconds when trading is over
      const interval = setInterval(() => {
        requestSettlementData();
      }, 5000);

      setSettlementPollingInterval(interval);

      // Initial request
      requestSettlementData();

      return () => {
        // Cleanup interval when component unmounts or isTradingOver becomes false
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clear interval when trading resumes
      if (settlementPollingInterval) {
        clearInterval(settlementPollingInterval);
        setSettlementPollingInterval(null);
      }
    }
  }, [isTradingOver, wsConnection]);

  // If no API key is set, show the API key input component
  if (!apiKey) {
    return <ApiKeyInput onConnectionSuccess={handleApiKeySuccess} />;
  }

  return (
    <>
    {isAdmin && <AdminPanel tradingStatus={tradingStatus} />}
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1 className="logo">Duke Trading Exchange</h1>
          {getCurrentUserId() && (
            <span className="team-badge">
              {getCurrentTeamName() ? (
                <>{getCurrentTeamName()} <span className="team-badge-uid">/ {getCurrentUserId()}</span></>
              ) : (
                getCurrentUserId()
              )}
            </span>
          )}
          <span className={`header-timer ${
            !tradingStatus.running ? "stopped" :
            tradingStatus.time_remaining === null ? "no-timer" :
            tradingStatus.time_remaining <= 30 ? "critical" :
            tradingStatus.time_remaining <= 60 ? "warning" : "normal"
          }`}>
            {tradingStatus.running
              ? tradingStatus.time_remaining !== null
                ? `${Math.floor(tradingStatus.time_remaining / 60).toString().padStart(2,"0")}:${Math.floor(tradingStatus.time_remaining % 60).toString().padStart(2,"0")}`
                : "Live"
              : "Ended"}
          </span>
        </div>
        <div className="header-right">
          {/* Portfolio Summary with Total Cash, Liquid Cash, and PnL */}
          {userError ? (
            <div className="portfolio-header-summary">
              <div className="portfolio-stat" style={{
                color: 'var(--sell-color)',
                fontWeight: 'bold',
                fontSize: '14px',
                padding: '8px 16px',
                backgroundColor: 'var(--sell-dim)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 61, 113, 0.3)',
                whiteSpace: 'nowrap'
              }}>
                ⚠️ {userError}
              </div>
            </div>
          ) : (
            <div className="portfolio-header-summary">
              <div className="portfolio-stat">
                <span className="stat-label">Total Cash:</span>
                <span className="stat-value">${Math.round(portfolio.cash?.total || 0)}</span>
              </div>
              <div className="portfolio-stat">
                <span className="stat-label">Liquid Cash:</span>
                <span className="stat-value">${Math.round(portfolio.cash?.unlocked || 0)}</span>
              </div>
              <div className="portfolio-stat">
                <span className="stat-label">PnL:</span>
                <span className={`stat-value ${
                  (() => {
                    const totalCash = portfolio.cash?.total || 0;
                    const startBal = portfolio.starting_balance || totalCash;
                    const pnl = totalCash > 0 ? totalCash - startBal : 0;
                    return pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : '';
                  })()
                }`}>
                  {(() => {
                    const totalCash = portfolio.cash?.total || 0;
                    const startBal = portfolio.starting_balance || totalCash;
                    const pnl = totalCash > 0 ? totalCash - startBal : 0;
                    return (pnl > 0 ? '+' : '') + '$' + Math.round(pnl);
                  })()}
                </span>
              </div>
            </div>
          )}
          <button className="order-history-button" onClick={() => setOrderHistoryOpen(true)}>
            Order History
          </button>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
          <div className="connection-status" title={`WebSocket: ${connectionStatus}`}>
            <span className={`connection-dot ${connectionStatus === "CONNECTED" ? "connected" : connectionStatus === "CONNECTING" ? "connecting" : "disconnected"}`} />
          </div>
        </div>
      </header>

      {isTradingOver ? (
        <div className="settlement-page">
          <h2>Trading is Over</h2>
          {settlementData?.status?.symbols?.length > 0 && (
            <div className="settlement-counters">
              <span>Symbols: {settlementData.status.symbols.join(', ')}</span>
            </div>
          )}
          <div className="settlement-container">
            <div className="settlement-values">
              <h3>Settlement Prices</h3>
              {settlementData?.settlement_values ? (
                Object.values(settlementData.settlement_values).some((v) => v !== null) ? (
                  Object.entries(settlementData.settlement_values).map(
                    ([contract, value]) => (
                      <div key={contract} className="settlement-value-item">
                        <span className="contract-name">{contract}</span>
                        <span className="settlement-value">${value}</span>
                      </div>
                    )
                  )
                ) : (
                  <div className="settlement-value-item">
                    <span className="contract-name">Awaiting settlement...</span>
                  </div>
                )
              ) : null}
            </div>
            <div className="settlement-summary">
              <h3>Your Results</h3>
              {settlementData?.final_cash != null ? (
                <>
                  <div className="settlement-amount">
                    Final Balance: ${settlementData.final_cash.toFixed(2)}
                  </div>
                  <div className={`settlement-pnl ${settlementData.pnl > 0 ? 'positive' : settlementData.pnl < 0 ? 'negative' : ''}`}>
                    PnL: {settlementData.pnl > 0 ? '+' : ''}${settlementData.pnl.toFixed(2)}
                  </div>
                </>
              ) : (
                <div className="settlement-amount">
                  Awaiting settlement...
                </div>
              )}
            </div>
          </div>
          {settlementData?.leaderboard?.length > 0 && (
            <div className="settlement-leaderboard">
              <h3>Leaderboard</h3>
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Team</th>
                    <th>PnL</th>
                    <th>Final Balance</th>
                    <th>Trades</th>
                  </tr>
                </thead>
                <tbody>
                  {settlementData.leaderboard.map((entry) => (
                    <tr key={entry.user_id} className={entry.user_id === apiKey ? "leaderboard-self" : ""}>
                      <td>#{entry.rank}</td>
                      <td>{entry.name}</td>
                      <td className={entry.pnl > 0 ? "positive" : entry.pnl < 0 ? "negative" : ""}>
                        {entry.pnl > 0 ? "+" : ""}${entry.pnl.toFixed(2)}
                      </td>
                      <td>${entry.final_cash.toFixed(2)}</td>
                      <td>{entry.matched_trades}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="main-content">
            <div className="app-content">
              <div className="main-trading-area">
                {caseActive && (
                  <DrawVisualization drawState={drawState} />
                )}
                <div className="order-book-grid">
                  {contracts.map((crypto) => (
                    <OrderBookCard
                      key={crypto.id}
                      symbol={crypto.id}
                      marketData={marketData[crypto.id]}
                      priceHistory={priceHistory[crypto.id] || []}
                      userOrders={(portfolio.active_trades || []).filter(
                        (t) => t.symbol === crypto.id
                      )}
                      fills={matchedTrades.filter((t) => t.symbol === crypto.id)}
                      isSelected={selectedContract === crypto.id}
                      onSelect={setSelectedContract}
                    />
                  ))}
                </div>
              </div>
              <div className="trade-sidebar">
                <TradeForm
                  wsConnection={wsConnection}
                  symbol={selectedContract}
                  portfolio={portfolio}
                />
                {caseActive && roleInfo && (
                  <RolePanel roleInfo={roleInfo} />
                )}
                <div className="positions-overview">
                  <h2>Portfolio</h2>
                  <div className="positions-list">
                    {(() => {
                      // Group fills by symbol
                      const fillsBySymbol = {};
                      matchedTrades.forEach((trade) => {
                        const sym = trade.symbol || trade.contractId;
                        if (!sym) return;
                        if (!fillsBySymbol[sym]) fillsBySymbol[sym] = [];
                        fillsBySymbol[sym].push(trade);
                      });

                      // Include symbols with only pending orders (no fills yet)
                      (portfolio.active_trades || []).forEach((t) => {
                        if (!fillsBySymbol[t.symbol]) fillsBySymbol[t.symbol] = [];
                      });

                      const symbols = Object.keys(fillsBySymbol).sort();
                      if (symbols.length === 0) {
                        return <div className="no-positions">No fills yet</div>;
                      }

                      return symbols.map((sym) => {
                        // Aggregate fills by side+price
                        const rawFills = fillsBySymbol[sym];
                        const fillMap = {};
                        rawFills.forEach((fill) => {
                          const key = `${fill.side}_${fill.price}`;
                          if (!fillMap[key]) {
                            fillMap[key] = { side: fill.side, price: fill.price, quantity: 0 };
                          }
                          fillMap[key].quantity += fill.quantity;
                        });
                        const fills = Object.values(fillMap);

                        const pendingBuys = (portfolio.active_trades || [])
                          .filter((t) => t.symbol === sym && t.side === "buy")
                          .reduce((s, t) => s + t.quantity, 0);
                        const pendingSells = (portfolio.active_trades || [])
                          .filter((t) => t.symbol === sym && t.side === "sell")
                          .reduce((s, t) => s + t.quantity, 0);
                        return (
                          <div key={sym} className={`position-group ${sym === selectedContract ? "active" : ""}`}
                               onClick={() => setSelectedContract(sym)}>
                            <div className="position-group-label">{sym}</div>
                            {fills.map((fill, i) => (
                              <div key={i} className={`position-fill-row ${fill.side}`}>
                                <span className={`fill-qty ${fill.side === "buy" ? "positive" : "negative"}`}>
                                  {fill.side === "buy" ? "+" : "-"}{fill.quantity}
                                </span>
                                <span className="fill-at">@</span>
                                <span className="fill-price">${Number(fill.price).toFixed(0)}</span>
                              </div>
                            ))}
                            {pendingBuys > 0 && (
                              <div className="position-pending buy">+{pendingBuys} pending</div>
                            )}
                            {pendingSells > 0 && (
                              <div className="position-pending sell">-{pendingSells} pending</div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {orderHistoryOpen && (
        <div className="modal-overlay" onClick={() => setOrderHistoryOpen(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setOrderHistoryOpen(false)}>×</button>
            <OrderHistory
              activeTrades={activeTrades}
              cancelledTrades={cancelledTrades}
              onCancelOrder={handleCancelOrder}
            />
          </div>
        </div>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
    </>
  );
}

export default App;
